import { motion } from 'framer-motion';
import { Compass, Heart, Users, TreePine } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen pt-32" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#F9F8F6]" data-testid="about-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              About Wild Thyme
            </p>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-[#2C332B] mb-8">
              Adventure and Wellbeing
              <br />
              Through <span className="text-[#D4A373]">Time Out</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#6C757D]">
              We believe everyone deserves meaningful time outdoors. Our guided experiences
              help you step away from daily pressures and reconnect with nature, yourself,
              and what truly matters.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-24 md:py-32" data-testid="brand-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <img
                src="https://customer-assets-agu9un31.emergentagent.net/job_2a8b6c11-5c0b-4843-b994-2c9faf61c357/artifacts/kqllrfkh_About%20Us2.jpeg"
                alt="Wild Thyme experience"
                className="w-full h-[500px] object-cover rounded-3xl shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
                Our Story
              </p>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B] mb-6">
                Born from a Love of the Outdoors
              </h2>
              <div className="space-y-4 text-lg leading-relaxed text-[#6C757D]">
                <p>
                  Wild Thyme started with a simple observation: in our busy, distracted world,
                  many of us are seeking something simple but powerful, mindful moments in nature or time to notice instead of rush.
                </p>
                <p>
                  We're not about extreme adventures or ticking off bucket lists. Instead, we
                  create thoughtfully guided experiences that help you slow down, breathe
                  deeply, and rediscover the restorative power of the outdoors, improving your overall wellbeing.
                </p>
                <p>
                  Whether you're seeking stress relief, a confidence boost, creative
                  inspiration, or simply quality time with like minded women, we're here to guide you
                  safely and warmly through experiences that truly matter.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy - Escape, Explore, Exhale */}
      <section className="py-24 md:py-32 bg-[#E8E4DD]" data-testid="philosophy-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Our Philosophy
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B]">
              Escape. Explore. Exhale.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Compass,
                title: 'Escape',
                description:
                  'Step away from screens, schedules, and everyday pressures. Create space for something different.',
              },
              {
                icon: TreePine,
                title: 'Explore',
                description:
                  'Discover new places, learn new skills, and explore parts of yourself that daily life doesn\'t always reveal.',
              },
              {
                icon: Heart,
                title: 'Exhale',
                description:
                  'Let go of stress, find calm, and leave feeling refreshed, restored, and reconnected.',
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 text-center"
                data-testid={`philosophy-card-${idx}`}
              >
                <div className="w-16 h-16 bg-[#3A4A3F] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl font-semibold text-[#2C332B] mb-4">
                  {item.title}
                </h3>
                <p className="text-[#6C757D] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 md:py-32" data-testid="approach-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              How We Work
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#2C332B] mb-6">
              Trusted, Personal, Flexible
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-[#6C757D]">
              Every experience is thoughtfully designed with your safety, comfort, and
              wellbeing at its heart.
            </p>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                title: 'Small Groups, Big Impact',
                description:
                  'We keep our groups small so every person feels seen, supported, and able to engage at their own pace.',
              },
              {
                title: 'Experienced Guides',
                description:
                  'All our guides are qualified, insured, and genuinely passionate about sharing the transformative power of sharing experiences.',
              },
              {
                title: 'Inclusive Experiences',
                description:
                  'Whether you\'re an outdoor novice or a seasoned adventurer, we have an experience that will suit you.',
              },
              {
                title: 'Outcome-Focused',
                description:
                  'We don\'t just "do" activities. Every experience is designed with clear wellbeing outcomes in mind—whether that\'s stress relief, confidence building, or creative renewal.',
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white/50 backdrop-blur-sm border border-white/60 rounded-3xl p-8"
                data-testid={`approach-item-${idx}`}
              >
                <h3 className="font-serif text-2xl font-semibold text-[#2C332B] mb-3">
                  {item.title}
                </h3>
                <p className="text-lg text-[#6C757D] leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team/Guides Section */}
      <section className="py-24 md:py-32 bg-[#3A4A3F] text-white" data-testid="team-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest font-bold text-white/60 mb-4">
              Meet Your Guides
            </p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-6">
              People Who Truly Care
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 max-w-3xl mx-auto mb-12">
              Our guides aren't just qualified professionals—they're warm, approachable
              people who genuinely care about creating meaningful experiences for you.
            </p>
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
              <Users className="w-5 h-5" />
              <p className="font-medium">Fully qualified, insured, and passionate</p>
            </div>
          </motion.div>

          {/* Owner Bios */}
          <div className="mt-20 max-w-5xl mx-auto">
            {[
              {
                name: 'Rachel',
                image: 'https://customer-assets.emergentagent.com/job_thyme-adventures/artifacts/o3ezkmv5_Rachel.png',
                bio: [
                  'Rachel is a UK-certified Behaviour Analyst with over 20 years\' experience supporting children and young people to develop meaningful, life-changing skills. Her work is grounded in science, compassion, and a deep belief that people thrive when the right environment and support are in place.',
                  'Before this chapter, Rachel served as aircrew in the RAF - shaping her calm, practical approach - and later worked in commercial sales, giving her a grounded understanding of both people and systems. Alongside her professional work, she is also a legal advocate supporting families through SEN tribunals.',
                  'Rachel is a single mum to three almost-grown sons, a lifelong learner, and happiest outdoors - wild swimming, hiking, beachcombing, or enthusiastically attempting life on a board. Wild Thyme brings together everything she values: community, courage, calm, and the space to breathe, reset, and reconnect with what truly matters.'
                ]
              },
              {
                name: 'Wendy',
                image: 'https://customer-assets.emergentagent.com/job_thyme-adventures/artifacts/7g3mf1ed_Wendy.jpg',
                bio: [
                  'Wendy is a qualified Empowerment Coach, Business Consultant, and international speaker with over 20 years\' senior leadership experience in private healthcare. She combines psychological insight with practical action, supporting individuals and teams to build clarity, confidence, and meaningful momentum.',
                  'Known for her grounded yet energising approach, Wendy helps people move beyond inherited limits, choose braver beliefs, and create lives and businesses that feel aligned, ethical, and powerful. Her work blends commercial reality with transformational depth, particularly around leadership, efficiency, and team cohesion.',
                  'Outside of work, Wendy is rarely still. She is an open-water swimmer, fire walk instructor, Ironwoman competitor, netball player and umpire, and a committed believer in trying one new thing every month. Wild Thyme reflects her belief that strength and softness can - and should - coexist.'
                ]
              },
              {
                name: 'Ginny',
                image: 'https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/g8nb43uo_Ginny2.jpg',
                bio: [
                  'Ginny brings strategic leadership, creativity, and a deep commitment to connection to Wild Thyme. With extensive experience leading work-winning and business functions within a major UK civil engineering organisation, she combines commercial clarity with a people-centred approach.',
                  'She is known for her ability to bring focus to complex situations, nurture collaboration, and create environments where people feel supported and confident to be themselves. Ginny values meaningful connection whilst encouraging fun, time outdoors, and the quieter rhythms that allow space for reflection and growth.',
                  'Outside of work she is mostly to be found in or close by to water, and spends time exploring the best swim spots in her campervan. Ginny has worked or volunteered in the outdoor pursuits industry since she was a teenager and holds leadership qualifications in Open Water Swimming, Moorland Walking, Mountain Biking, Kayaking, Sailing and Archery.',
                  'At Wild Thyme, Ginny helps shape experiences that feel grounded, welcoming, and deeply human - creating spaces where women can pause, breathe, and reconnect with what truly matters.'
                ]
              }
            ].map((person, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="mb-16 last:mb-0"
                data-testid={`bio-${person.name.toLowerCase()}`}
              >
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  {/* Headshot Image */}
                  <div className="flex-shrink-0 w-full md:w-48">
                    <img
                      src={person.image}
                      alt={`${person.name} - Wild Thyme Guide`}
                      className="w-48 h-48 rounded-full object-cover mx-auto md:mx-0 shadow-lg"
                    />
                  </div>
                  
                  {/* Bio Content */}
                  <div className="flex-1 text-left">
                    <h3 className="font-serif text-2xl md:text-3xl font-semibold mb-6">
                      {person.name}
                    </h3>
                    <div className="space-y-4">
                      {person.bio.map((paragraph, pIdx) => (
                        <p key={pIdx} className="text-lg leading-relaxed text-white/90">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
